import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@heroui/button";
import { Input } from "@heroui/input";

import { ordersService } from "../../services/ordersService";
import { OrderSummaryResponse } from "../../types/orders";
import PageTitle from "../../components/common/PageTitle";
import OrdersTable from "../../components/orders/OrdersTable";
import OrdersFilter from "../../components/orders/OrdersFilter";

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<OrderSummaryResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<{
    status_id?: number;
    order_type?: string;
    start_date?: string;
    end_date?: string;
  }>({});

  // Fetch orders
  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ordersService.getOrders({
        skip: (page - 1) * pageSize,
        limit: pageSize,
        search: searchQuery,
        ...filters,
      });

      setOrders(response.orders);
      setTotal(response.total);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      setError("Failed to load orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch orders when page, pageSize, or filters change
  useEffect(() => {
    fetchOrders();
  }, [page, pageSize, filters]);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page
    fetchOrders();
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page
  };

  // Navigate to order details
  const handleViewOrder = (orderId: number) => {
    navigate(`/orders/${orderId}`);
  };

  return (
    <div className="space-y-6">
      <PageTitle
        actions={
          <Button color="primary" onPress={() => navigate("/orders/create")}>
            Create Order
          </Button>
        }
        subtitle="Manage and track all customer orders"
        title="Orders"
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters (left side) */}
        <div className="lg:col-span-1">
          <OrdersFilter
            initialFilters={filters}
            onFilterChange={handleFilterChange}
          />
        </div>

        {/* Orders table (right side) */}
        <div className="lg:col-span-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
            {/* Search bar */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <form className="flex gap-2" onSubmit={handleSearch}>
                <Input
                  className="flex-1"
                  placeholder="Search by order number"
                  size="sm"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button
                  color="primary"
                  isLoading={loading && !!searchQuery}
                  size="sm"
                  type="submit"
                >
                  Search
                </Button>
              </form>
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 m-4 rounded-lg">
                {error}
              </div>
            )}

            {/* Orders table */}
            <OrdersTable
              loading={loading}
              //@ts-ignore
              orders={orders}
              pagination={{
                page,
                pageSize,
                total,
              }}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onViewOrder={handleViewOrder}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
